import { useRouter } from 'next/router'
import React, { Suspense } from 'react'

import ListWithIcon from '@/legacy-src/components/ListIcon'
import TherapeuticStyleProfile from '@/legacy-src/components/recommendedTherapist/TherapeuticStyleProfile'
import { TherapistBioBillingInfo } from '@/legacy-src/components/TherapistBioBillingInfo'

import { RecommendedTherapistProfileBioSession } from '@/src/domains/recommended-therapist/components/RecommendedTherapistProfileBio/RecommendedTherapistProfileBioSession'

import { isRoutePublicTherapistList } from '../constants/locale.constants'
import useReviews from '../hooks/useReviews'
import useSpecializations from '../hooks/useSpecializations'
import useTherapist from '../hooks/useTherapist'
import { getTranslate } from '../utilities/i18n'
import InsuranceCompanyList from './insuranceCompanies/InsuranceCompanyList'
import ReviewsList from './ReviewsList'
import SpecializationMapper from './SpecializationMapper'
import YoutubeVideo from './YoutubeVideo'

type TherapistBioPropsType = {
  userType: number
  therapistId: number
}

const PinkHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <h3 className="m-0 mb-3 !inline-block rounded-xl bg-pink-500 px-1 py-1 text-xl leading-7">{children}</h3>
}

const TherapistBio: React.FC<TherapistBioPropsType> = ({ userType, therapistId }) => {
  const router = useRouter()
  const { therapist } = useTherapist(therapistId)
  const { t } = getTranslate()
  const { reviews, countOfPositiveTextReviews } = useReviews(therapistId)
  const { specializations } = useSpecializations()

  if (!therapist) {
    return null
  }

  const { description, education, certificates, majorSpecializations, minorSpecializations, videoId, recommendations } =
    therapist

  const isPublic = isRoutePublicTherapistList(router.route)

  return (
    <div>
      {/* anchor needs to be a bit higher, so the title is not under header when scrolled to */}
      <div id="bio" className="relative -top-[80px] big-tablet:-top-[20px]" />
      <div className="flex flex-wrap gap-8 tablet:flex-nowrap tablet:gap-20">
        <div className="basis-full tablet:basis-1/2">
          {!isPublic && (
            <TherapeuticStyleProfile
              therapistId={therapist.therapistId}
              title={t(`component.recommendedTherapist.styleHeadline`)}
              className="mb-8"
            />
          )}

          <SpecializationMapper
            majorSpecializations={majorSpecializations}
            minorSpecializations={minorSpecializations}
            specializationOptions={specializations}
            t={t}
          />
          {videoId && (
            <>
              <h3>{t('therapistProfile.video')}</h3>
              <div className="m-0 mb-8 overflow-hidden rounded-3xl">
                <YoutubeVideo videoId={videoId} />
              </div>
            </>
          )}
          <RecommendedTherapistProfileBioSession therapist={therapist} />
          <div className="mb-8" />
          <InsuranceCompanyList therapistId={therapist.therapistUserId} />
          <ReviewsList
            countOfPositiveReviews={countOfPositiveTextReviews}
            recommendations={recommendations}
            reviews={reviews}
            t={t}
            userType={userType}
          />
        </div>
        <div className="basis-full tablet:basis-1/2">
          <div className="flex flex-col gap-7 rounded-3xl bg-white p-5">
            {description && (
              <div>
                <PinkHeading>{t('therapistProfile.description')}</PinkHeading>
                <div className="text-base">
                  <span>{description}</span>
                </div>
              </div>
            )}
            {education && (
              <div>
                <PinkHeading>{t('therapistProfile.education')}</PinkHeading>
                <div className="text-base">
                  <p>{education}</p>
                </div>
              </div>
            )}
            {Array.isArray(certificates) && (
              <div>
                <PinkHeading>{t('therapistProfile.certificates')}</PinkHeading>
                <ListWithIcon items={certificates} iconName="certificate" />
              </div>
            )}
            {process.env.NEXT_PUBLIC_LOCALE === 'sk' && (
              <div>
                <h3 className="mt-6">Poskytuje sa prostredníctvom Hedepy zdravotná starostlivosť?</h3>
                <p className="text-base">
                  Hedepy služby priamo neposkytuje, iba sprostredkúva kontakt s odborníkmi. Či ide o zdravotnú
                  starostlivosť, závisí od ich špecializácie. <strong>Individuálne konzultácie</strong> slúžia na
                  podporu duševnej pohody a <em>nie sú</em> zdravotnou starostlivosťou v zmysle Zákona č. 576/2004 Z. z.
                  Zdravotnú starostlivosť poskytujú svojim klientom priamo odborníci v kategórii{' '}
                  <strong>klinická psychológia a psychoterapia</strong>.
                </p>
              </div>
            )}
          </div>
          {process.env.NEXT_PUBLIC_LOCALE === 'sk' ? (
            <Suspense>
              <TherapistBioBillingInfo therapistUserId={therapist.therapistUserId} />
            </Suspense>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default TherapistBio
